// src/routes/config.ts
import { lazy } from "react";
import WalletGuard from "../guards/walletGuard.js";
import IncomeExpenseReport from "../reports/IncomeExpenseReport/index.js";

// Lazy loaded pages
const DashboardV1 = lazy(() => import("../pages/user/DashboardV1"));
const AmenitiesDashboard = lazy(() =>
  import("../pages/user/Dashboard/AmenitiesDashboard")
);
const AmenitiesDetailsPage = lazy(() =>
  import("../pages/user/Dashboard/AmenitiesDashboard/AmenitiesDetailsPage")
);
const AmenitiesBookingPage = lazy(() =>
  import("../pages/user/Dashboard/AmenitiesDashboard/AmenitiesBookingPage")
);
const Management = lazy(() => import("../pages/user/Management"));
const Amenities = lazy(() => import("../pages/user/Management/Amenities"));
const SetupAccount = lazy(() => import("../pages/user/Accounts/SetupAccount"));
const AccountDetail = lazy(() =>
  import("../pages/user/Accounts/SetupAccount/AccountsDetailPage")
);
const SetupExpense = lazy(() => import("../pages/user/Accounts/SetupExpense"));
const ExpenseDetailPage = lazy(() =>
  import("../pages/user/Accounts/SetupExpense/ExpenseDetailPage")
);
const ExpenseAndCategoryDetail = lazy(() =>
  import("../pages/user/Accounts/SetupExpense/ViewExpenseDetail")
);
const GenerateBillPage = lazy(() =>
  import("../pages/user/Bills/GenerateBillPage")
);
const BillsCollectionPage = lazy(() =>
  import("../pages/user/Bills/BillsCollectionPage")
);
const UsersPage = lazy(() => import("../pages/user/UsersPage"));
const UsersDetailsPage = lazy(() =>
  import("../pages/user/UsersPage/UsersDetailsPage")
);
const UserRole = lazy(() => import("../pages/user/Management/user-role"));
const RolesFormPage = lazy(() => import("../pages/user/RolesFormPage"));
const Announcements = lazy(() =>
  import("../pages/user/Settings/Announcements")
);
const Complaints = lazy(() => import("../pages/user/Settings/Complains"));
const Receptionist = lazy(() => import("../pages/user/Receptionist"));
const BankApprovalPage = lazy(() =>
  import("../pages/user/Settings/Approvals/BankApprovalPage")
);
const VisitorApprovalPage = lazy(() =>
  import("../pages/user/Settings/Approvals/VisitorApprovalsPage")
);
const JoinApprovalsPage = lazy(() =>
  import("../pages/user/Settings/Approvals/JoinApprovalsPage")
);
const BankapprovalDetailPage = lazy(() =>
  import("../pages/user/Settings/BankApprovalDetail")
);
const VisitorApprovalDetailPage = lazy(() =>
  import("../pages/user/Settings/VisitorApprovalDetail")
);
const JoinApprovalDetailPage = lazy(() =>
  import("../pages/user/Settings/JoinApprovalDetail")
);
const CommunitySettings = lazy(() =>
  import("../pages/user/Settings/CommunitySettings")
);
const AdditionalSettings = lazy(() =>
  import("../pages/user/Settings/AdditionalSettings")
);
const CommunityPolicy = lazy(() => import("../pages/user/CommunityPolicy"));
const PaymentHistory = lazy(() => import("../pages/user/PaymentHistory"));
const ResidentPayments = lazy(() => import("../pages/user/ResidentPayments"));
const ResidentTransactionsDetailsPage = lazy(() =>
  import("../pages/user/ResidentPayments/ResidentTransactionsDetailsPage")
);
const ResidentVisitors = lazy(() => import("../pages/user/ResidentVisitors"));
const VisitorsDetailsPage = lazy(() =>
  import("../pages/user/ResidentVisitors/VisitorsDetailsPage")
);
const Wallet = lazy(() => import("../pages/user/Wallet"));
const TransactionHistory = lazy(() =>
  import("../pages/user/TransactionHistory")
);
const Beneficiary = lazy(() => import("../pages/user/Beneficiary"));
const Withdraw = lazy(() => import("../pages/user/Withdraw"));
const ScheduleOfChargesPage = lazy(() =>
  import("../pages/user/ScheduleOfCharges")
);
const Reports = lazy(() => import("../pages/user/Reports"));
const MaintenanceReport = lazy(() => import("../pages/user/MaintenanceReport"));
const DuesReportPage = lazy(() => import("../pages/user/DuesReportPage"));
const ResidentAnnouncementsListings = lazy(() =>
  import("../pages/user/AnnouncementsResidentListing")
);
const Help = lazy(() => import("../pages/user/Helps"));
const ExpenseReport = lazy(() => import("../reports/ExpenseReport"));
const CommunityLedger = lazy(() =>
  import("../reports/CommunitySummaryLedgerReport")
);
const ExpenseSingleReport = lazy(() =>
  import("../reports/CommunitySingleLedger")
);
const AllDuesReport = lazy(() => import("../reports/duesReport/index.js"));
// Standalone pages (no UserLayout)
const CreateOrJoinCommunity = lazy(() =>
  import("../pages/user/CreateOrJoinCommunity")
);
const SubscriptionTimer = lazy(() => import("../pages/user/SubscriptionTimer"));
const ChangePassword = lazy(() =>
  import("../pages/user/Settings/ChangePassword")
);
const Complains = lazy(() => import("../pages/user/Settings/Complains"));
const Notifications = lazy(() =>
  import("../pages/user/Settings/Notifications")
);
const PrivacyPolicy = lazy(() =>
  import("../pages/user/Settings/PrivacyPolicy")
);
const TermsNConditions = lazy(() =>
  import("../pages/user/Settings/TermsNConditions")
);
const SubscriptionUpdate = lazy(() =>
  import("../pages/user/SubscriptionUpdate")
);
const Subscription = lazy(() => import("../pages/user/Subscription"));
export const protectedRoutes = [
  { path: "/", element: <DashboardV1 /> },
  { path: "/amenityDashboard", element: <AmenitiesDashboard /> },
  { path: "/amenityDetails/:id", element: <AmenitiesDetailsPage /> },
  { path: "/amenityBooking/:id", element: <AmenitiesBookingPage /> },
  { path: "/management", element: <Management /> },
  { path: "/ammenities", element: <Amenities /> },
  { path: "/accounts", element: <SetupAccount /> },
  { path: "/accounts/:accountName/:accountCode", element: <AccountDetail /> },
  { path: "/expense", element: <SetupExpense /> },
  {
    path: "/expense/:categoryCode/:subCategoryCode",
    element: <ExpenseDetailPage />,
  },
  {
    path: "/expense/:categoryCode/:expenseName/subcategory",
    element: <ExpenseAndCategoryDetail />,
  },
  { path: "/generateBill", element: <GenerateBillPage /> },
  { path: "/paymentCollection", element: <BillsCollectionPage /> },
  { path: "/users", element: <UsersPage /> },
  { path: "/users/:id", element: <UsersDetailsPage /> },
  { path: "/userRole", element: <UserRole /> },
  { path: "/userRole/new", element: <RolesFormPage mode="new" /> },
  { path: "/userRole/:id", element: <RolesFormPage mode="edit" /> },
  { path: "/announcements", element: <Announcements /> },
  { path: "/complaints", element: <Complains /> },
  { path: "/receptionist", element: <Receptionist /> },
  {
    path: "/bank-approval",
    element: <BankApprovalPage type={4} name="Bank Approvals" />,
  },
  { path: "/bank-approval/:id", element: <BankapprovalDetailPage /> },
  {
    path: "/visitor-approval",
    element: <VisitorApprovalPage type={3} name="Visitor Approvals" />,
  },
  {
    path: "/visitor-approval/:id",
    element: <VisitorApprovalDetailPage name="Visitor Approvals" />,
  },
  {
    path: "/join-approval",
    element: <JoinApprovalsPage type={1} name="Join Approvals" />,
  },
  {
    path: "/join-approval/:id",
    element: <JoinApprovalDetailPage type={1} name="Join Approvals" />,
  },
  { path: "/communitySettings", element: <CommunitySettings /> },
  { path: "/additionalSettings", element: <AdditionalSettings /> },
  { path: "/communityPolicy", element: <CommunityPolicy /> },
  { path: "/paymentHistory", element: <PaymentHistory /> },
  { path: "/payments", element: <ResidentPayments /> },
  { path: "/payments/:id", element: <ResidentTransactionsDetailsPage /> },
  { path: "/visitors", element: <ResidentVisitors /> },
  { path: "/visitors/:id", element: <VisitorsDetailsPage /> },
  { path: "/reports", element: <Reports /> },

  // Wallet Routes
  { path: "/wallet", element: <Wallet /> },
  { path: "/transaction-history", element: <TransactionHistory /> },
  {
    path: "/beneficiaries",
    element: (
      <WalletGuard>
        <Beneficiary />
      </WalletGuard>
    ),
  },
  {
    path: "/withdraw",
    element: (
      <WalletGuard>
        <Withdraw />
      </WalletGuard>
    ),
  },
  {
    path: "/schedule-of-charges",
    element: (
      <WalletGuard>
        <ScheduleOfChargesPage />
      </WalletGuard>
    ),
  },

  // Reports
  {
    path: "/maintenance-report/:paymentMasterId",
    element: <MaintenanceReport />,
  },
  {
    path: "/dues-report/:communitySeriesDetailID",
    element: <DuesReportPage />,
  },
  {
    path: "/report/expense/:fromDate/:toDate/:accountCode",
    element: <ExpenseReport />,
  },
  {
    path: "/report/accounts/:fromDate/:toDate/:accountCode",
    element: <CommunityLedger />,
  },
  {
    path: "/report/account/:accountName/:fromDate/:toDate/:accountCode",
    element: <ExpenseSingleReport />,
  },
  { path: "/report/current-dues-report", element: <AllDuesReport /> },
  {path:"/report/income-expense-report/:fromDate/:toDate",element:<IncomeExpenseReport />},
  // Always allowed
  {
    path: "/resident-announcements",
    element: <ResidentAnnouncementsListings />,
  },
  { path: "/helps", element: <Help /> },
  {
    path: "/changePassword",
    element: <ChangePassword />,
  },
  {
    path: "/termsNConditions",
    element: <TermsNConditions />,
  },
  { path: "/privacyPolicy", element: <PrivacyPolicy /> },
  { path: "/notifications", element: <Notifications /> },
  { path: "/subscription", element: <Subscription /> },
  { path: "/subscription-update", element: <SubscriptionUpdate /> },
];

// Standalone routes (outside UserLayout)
export const standaloneRoutes = [
  { path: "/create-community", element: <CreateOrJoinCommunity /> },
  { path: "/subscription-timer", element: <SubscriptionTimer /> },
];
